import type { Entity, HistoryFee, HistoryMonth, OtherCostMonthRow } from "./api";
import { COMPONENT_LABEL, monthsBetween } from "./format";

// C5 v2.6 (spec B, B11): the Cost History drawer renders every fee row the engine serves (B10, months[].fees).
// C5 v2.8 (spec B v1.2): every row sits in its OFS channel group; a row with parent_code is a sub-row of its leaf
// (the Shopify fulfilment leaf's per-order and residual parts) and never counts in a subtotal or in Total Fee.
// This module only groups and sums what is served: no row is dropped, reordered or recomputed in the browser.
export type FeeRow = { code: string; label: string; parent: boolean; perUnit: Record<string, number | null> };
export type FeeBlock = { group: string; label: string; subtotal: boolean; rows: FeeRow[]; subtotalPerUnit: Record<string, number | null> };
export type FeeSeries = { key: string; name: string };
export type ProjectionWindow = { month: string; window: string[]; consecutive: boolean } | null;

// C4 v1.6.4 (Bojan, 1 Nov 2026): Expected (trailing 3) weights the newest used month 50%, the one before 30%, the oldest 20%.
// Fewer than three used months: the first weights in that order, rescaled to sum to 1 (two months 0.625 / 0.375, one month 1).
export const TRAILING_WEIGHTS = [0.5, 0.3, 0.2] as const;

// C4 v1.6.5: one entry per month of the tab's period; a month PFC served nothing for gets a placeholder with no fee and no
// units. The caller computes the Expected window from the served months, never from this list.
export function padHistoryMonths(months: HistoryMonth[], from: string, to: string, currency: string): { months: HistoryMonth[]; noData: ReadonlySet<string> } {
  const byMonth = new Map(months.map((month) => [month.month.slice(0, 7), month] as const));
  const period = monthsBetween(from, to);
  const missing = period.filter((month) => !byMonth.has(month));
  if (missing.length === 0) return { months, noData: new Set<string>() };
  const padded = period.map((month): HistoryMonth => byMonth.get(month) ?? {
    month, units: 0, orders: null, total: "0", per_unit: null, currency, components: {},
    change: null, change_pct: null, beyond_band: null, fees: [],
  });
  return { months: padded, noData: new Set(missing) };
}

// C4 v1.6.5: how many months back the close list must reach to cover `from`, counted the engine's way (from the first day
// of `thisMonth`); at least 6 (today's value), at most 36 (the engine's limit).
export function closesMonthsBack(from: string, thisMonth: string): number {
  return Math.min(36, Math.max(6, monthsBetween(from, thisMonth).length));
}

// used: "YYYY-MM" months in ascending order (every caller passes window months, which are ascending). Returns month -> weight,
// keys newest first. No sorting call: the C5 v2.6 h8 / v2.8 h6 source tests forbid one in historyFeesModel.ts.
export function trailingWeights(used: string[]): Record<string, number> {
  const newestFirst = used.slice(-3).reverse();
  const raw = newestFirst.map((_, index) => TRAILING_WEIGHTS[index] ?? 0);
  const sum = raw.reduce((total, weight) => total + weight, 0);
  return Object.fromEntries(newestFirst.map((month, index) => [month, (raw[index] ?? 0) / sum]));
}

// "weighted 50/30/20, newest first" (3 months), "weighted 62.5/37.5, newest first" (2), null (0 or 1).
export function weightLabel(used: string[]): string | null {
  if (used.length < 2) return null;
  return `weighted ${Object.values(trailingWeights(used)).map((weight) => Number((weight * 100).toFixed(1))).join("/")}, newest first`;
}

export function hasFees(months: HistoryMonth[]): boolean {
  return months.some((month) => month.fees !== undefined);
}

// A sub-row that carries an exact component keeps the words the Monitor and Rates tabs use (COMPONENT_LABEL): Pick & Pack,
// Packaging, Fulfilment Fee under the Shopify fulfilment leaf. A leaf row keeps the engine's label even when it merges an exact
// component (Channel Amazon - Transaction Fees holds the referral fee and its spread part). A v1.0-shaped response
// (settlement / order_3pl codes) is read the same way as a sub-row.
export function rowLabel(fee: HistoryFee): string {
  const legacy = fee.group === "settlement" || fee.group === "order_3pl";
  const component = fee.parent_code !== undefined || legacy ? fee.component ?? (legacy ? fee.code : undefined) : undefined;
  if (component === undefined) return fee.label;
  return (COMPONENT_LABEL as Record<string, string | undefined>)[component] ?? fee.label;
}

function sumOrNull(values: Array<number | null>): number | null {
  const present = values.filter((value): value is number => value !== null);
  return present.length ? present.reduce((sum, value) => sum + value, 0) : null;
}

// One block per engine group, in the engine's order, every block with a subtotal row; a row's per-unit value is the served per_unit
// (null when the engine serves none). A sub-row keeps its parent's place: it follows its parent in served order.
export function feeBlocks(months: HistoryMonth[]): FeeBlock[] {
  const blocks: FeeBlock[] = [];
  for (const month of months) {
    for (const fee of month.fees ?? []) {
      let block = blocks.find((candidate) => candidate.group === fee.group);
      if (!block) {
        block = { group: fee.group, label: fee.group_label, subtotal: true, rows: [], subtotalPerUnit: {} };
        blocks.push(block);
      }
      if (!block.rows.some((row) => row.code === fee.code)) block.rows.push({ code: fee.code, label: rowLabel(fee), parent: fee.parent_code === undefined, perUnit: {} });
    }
  }
  for (const block of blocks) {
    for (const month of months) {
      for (const row of block.rows) {
        const fee = month.fees?.find((candidate) => candidate.group === block.group && candidate.code === row.code);
        row.perUnit[month.month] = fee === undefined || fee.per_unit === null ? null : Number(fee.per_unit);
      }
      block.subtotalPerUnit[month.month] = month.fees === undefined ? null : sumOrNull(block.rows.filter((row) => row.parent).map((row) => row.perUnit[month.month] ?? null));
    }
  }
  return blocks;
}

// Total Fee is the sum of every row without a parent, never of sub-rows or subtotals; a month the engine served without fees has no total.
export function feeTotal(blocks: FeeBlock[], month: HistoryMonth): number | null {
  if (month.fees === undefined) return null;
  return sumOrNull(blocks.flatMap((block) => block.rows.filter((row) => row.parent).map((row) => row.perUnit[month.month] ?? null)));
}

// Chart lines: one per block (its subtotal). The dialog adds Total Fee.
export function feeChartSeries(blocks: FeeBlock[]): FeeSeries[] {
  return blocks.map((block) => ({ key: `block:${block.group}`, name: block.label }));
}

export function feeChartRows(blocks: FeeBlock[], months: HistoryMonth[]): Array<Record<string, string | number | null>> {
  return months.map((month) => ({
    month: month.month,
    total: feeTotal(blocks, month),
    ...Object.fromEntries(blocks.map((block) => [`block:${block.group}`, block.subtotalPerUnit[month.month] ?? null])),
  }));
}

// C4 v1.6.3: the Cost History chart splits each block into its costs (rows above zero) and its credits (rows below zero),
// so a credit such as Inventory Reimbursement draws below the axis in its group's colour. Costs + credits = subtotalPerUnit.
export function feeSignedChartRows(blocks: FeeBlock[], months: HistoryMonth[]): Array<Record<string, string | number | null>> {
  return months.map((month) => {
    const row: Record<string, string | number | null> = { month: month.month };
    for (const block of blocks) splitInto(row, block.group, block.rows.filter((fee) => fee.parent).map((fee) => fee.perUnit[month.month] ?? null));
    return row;
  });
}

function splitInto(row: Record<string, string | number | null>, group: string, values: Array<number | null>): void {
  const present = values.filter((value): value is number => value !== null);
  const costs = present.filter((value) => value > 0);
  const credits = present.filter((value) => value < 0);
  row[`block:${group}:costs`] = costs.length ? costs.reduce((sum, value) => sum + value, 0) : null;
  row[`block:${group}:credits`] = credits.length ? credits.reduce((sum, value) => sum + value, 0) : null;
}

// C4 v1.6.3 (DEC-1380 PB-54): the PFC drawer's Expected (trailing 3) column. Mirrors C4 v1.6.2's expectedWindow: the last
// up-to-three months in view that are served AND closed for the key's entity (month close, GET /other-costs/months).
const CHANNEL_ENTITY: Record<string, Entity> = {
  amazon_uk: "LTD", shopify_uk: "LTD", wholesale_uk: "LTD",
  amazon_eu: "OU", shopify_eu: "OU", wholesale_eu: "OU",
  amazon_ae: "LLC", shopify_mena: "LLC", wholesale_mena: "LLC",
};

function nextMonth(month: string): string {
  const [year, mm] = month.slice(0, 7).split("-").map(Number);
  return mm === 12 ? `${year + 1}-01` : `${year}-${String(mm + 1).padStart(2, "0")}`;
}

export function projectionWindow(months: HistoryMonth[], closes: OtherCostMonthRow[] | undefined, channelLocation: string): ProjectionWindow {
  const entity = CHANNEL_ENTITY[channelLocation];
  if (!entity || !closes) return null;
  const closed = new Set(closes.filter((row) => row.entity_id === entity && row.status === "closed").map((row) => row.month.slice(0, 7)));
  const eligible = months.map((month) => month.month.slice(0, 7)).filter((month) => closed.has(month));
  if (eligible.length === 0) return null;
  const window = eligible.slice(-3);
  const consecutive = window.length === 3 && nextMonth(window[0]) === window[1] && nextMonth(window[1]) === window[2];
  return { month: window[window.length - 1], window, consecutive };
}

// C4 v1.6.4: window months with units sold, ascending.
export function usedProjectionMonths(months: HistoryMonth[], w: NonNullable<ProjectionWindow>): string[] {
  return w.window.filter((window) => months.some((month) => month.month.slice(0, 7) === window && month.units > 0));
}

// C4 v1.6.4 (Bojan, 1 Nov 2026): 50/30/20 over the used window months, newest first (trailingWeights). A used month in which the
// row has no fee counts as 0, as before. null when the row has no fee in any used month, or no window month has units.
export function rowProjection(row: FeeRow, months: HistoryMonth[], w: NonNullable<ProjectionWindow>): number | null {
  const used = months.filter((month) => w.window.includes(month.month.slice(0, 7)) && month.units > 0);
  if (!used.some((month) => row.perUnit[month.month] !== null && row.perUnit[month.month] !== undefined)) return null;
  const weights = trailingWeights(used.map((month) => month.month.slice(0, 7)));
  return used.reduce((sum, month) => sum + (weights[month.month.slice(0, 7)] ?? 0) * (row.perUnit[month.month] ?? 0), 0);
}

export function blockProjection(block: FeeBlock, months: HistoryMonth[], w: NonNullable<ProjectionWindow>): number | null {
  return sumOrNull(block.rows.filter((row) => row.parent).map((row) => rowProjection(row, months, w)));
}

export function projectionTotal(blocks: FeeBlock[], months: HistoryMonth[], w: NonNullable<ProjectionWindow>): number | null {
  return sumOrNull(blocks.flatMap((block) => block.rows.filter((row) => row.parent).map((row) => rowProjection(row, months, w))));
}

export function expectedFeeChartRow(blocks: FeeBlock[], months: HistoryMonth[], w: ProjectionWindow): Record<string, string | number | null> | null {
  if (!w) return null;
  const row: Record<string, string | number | null> = { month: "Expected (trailing 3)", expected: 1 };
  for (const block of blocks) splitInto(row, block.group, block.rows.filter((fee) => fee.parent).map((fee) => rowProjection(fee, months, w)));
  return row;
}
